import { FileType, UploadedFile } from "@inrev/common";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { UseMutateFunction, useMutation } from "@tanstack/react-query";
import { lookup } from "mrmime";
import { useRef } from "react";
import { UploadFile } from "../../../types";
import { asyncSleep } from "../../../utils";
import { logError, resolveErrorMessage } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { FileUploadCallbacks } from "./types";

export type FileUploadOptions<T, TFileType extends FileType = FileType> = FileUploadCallbacks<
	TFileType,
	T
> & {
	suppressUploadErrorModal?: boolean;
};

export type UseUpload<T, TFileType extends FileType = FileType> = (
	callbacks: FileUploadOptions<T, TFileType>,
) => {
	uploadFn: UseMutateFunction<
		T,
		unknown,
		{ fileUploads: { rawFile: File; pendingFile: UploadFile<TFileType> }[] },
		unknown
	>;
	updateFn: UseMutateFunction<T, unknown, { pendingFile: UploadedFile<TFileType> }, unknown>;
	deleteFn: UseMutateFunction<void, unknown, { id: string }, unknown>;
	fileLoading: boolean;
};

export const useFileUpload = <TFileType extends FileType = FileType>(
	callbacks: FileUploadOptions<UploadedFile<TFileType>[], TFileType>,
) => {
	const cbRef = useRef(callbacks);
	cbRef.current = callbacks;
	const { post, patch } = useRequest();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const uploadFileMutation = useMutation({
		mutationFn: async (args: {
			fileUploads: { rawFile: File; pendingFile: UploadFile<TFileType> }[];
		}): Promise<UploadedFile<TFileType>[]> => {
			return await Promise.all(
				args.fileUploads.map(async (fileUpload) => {
					const data = new FormData();
					let fileToUpload = fileUpload.rawFile;
					if (!fileUpload.rawFile.type) {
						const mime = lookup(fileUpload.rawFile.name);
						if (mime) {
							fileToUpload = new File([fileUpload.rawFile], fileUpload.rawFile.name, {
								type: mime,
							});
						}
					}
					data.append("file", fileToUpload);
					if (fileUpload.pendingFile.types && fileUpload.pendingFile.types?.length > 0) {
						data.append("types", fileUpload.pendingFile.types.join(","));
					}
					return await post<UploadedFile<TFileType>>("/v2/files", data);
				}),
			);
		},
		onSuccess: (data, args) => {
			cbRef.current.onUploadSuccess(data, args);
		},
		onError: (error: Error, args) => {
			cbRef.current.onUploadError(error, args);
			logError(error);
			if (!cbRef.current.suppressUploadErrorModal) {
				triggerErrorModal(error, resolveErrorMessage(error));
			}
		},
	});

	const updateFileMutation = useMutation({
		mutationFn: async (args: {
			pendingFile: UploadedFile<TFileType>;
		}): Promise<UploadedFile<TFileType>[]> => {
			const response = await patch<UploadedFile<TFileType>>(
				`/v2/files/${args.pendingFile.id}`,
				args.pendingFile.types,
			);
			return [response];
		},
		onSuccess: (data, args) => {
			cbRef.current.onUpdateSuccess?.(data, args);
		},
		onError: (error: Error, args) => {
			cbRef.current.onUpdateError?.(error, args);
			logError(error);
			triggerErrorModal(error, resolveErrorMessage(error));
		},
	});

	const deleteFileMutation = useMutation({
		mutationFn: async (_args: { id: string }) => {
			// TODO: implement deleteFile
			await asyncSleep(750);
		},
		onSuccess: (_data, args) => {
			cbRef.current.onDeleteSuccess?.(args);
		},
		onError: (error: Error, args) => {
			logError(error);
			cbRef.current.onDeleteError?.(error, args);
		},
	});

	return {
		uploadFn: uploadFileMutation.mutate,
		updateFn: updateFileMutation.mutate,
		deleteFn: deleteFileMutation.mutate,
		fileLoading:
			uploadFileMutation.isPending || updateFileMutation.isPending || deleteFileMutation.isPending,
	};
};
